import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Newspaper, FileText, ChevronLeft, BarChart3, SlidersHorizontal, Cable, Building2, Home, LogOut, ChevronsUpDown, Search, Check, Layers } from 'lucide-react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Avatar } from './ui/avatar';
import { clients } from '../lib/data';
interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}
const navSections = [{
  title: null,
  items: [{
    icon: Home,
    label: 'Panoramica',
    path: '/home'
  }, {
    icon: Newspaper,
    label: 'Rassegne',
    path: '/rassegne'
  }, {
    icon: BarChart3,
    label: 'Statistiche',
    path: '/statistics'
  }, {
    icon: FileText,
    label: 'Report',
    path: '/reports'
  }]
}, {
  title: 'CONFIGURAZIONE',
  items: [{
    icon: SlidersHorizontal,
    label: 'Algoritmo',
    path: '/settings/algorithm'
  }, {
    icon: Cable,
    label: 'Connettori',
    path: '/settings/connectors'
  }, {
    icon: Layers,
    label: 'Gestione Tier',
    path: '/settings/tiers'
  }]
}];
export function Sidebar({
  collapsed,
  onToggle
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedClientId, setSelectedClientId] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('selectedClientId') || 'etoro';
    }
    return 'etoro';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('selectedClientId', selectedClientId);
    }
  }, [selectedClientId]);
  // Sync if another part of the app changes the client
  useEffect(() => {
    const handler = () => {
      const id = localStorage.getItem('selectedClientId');
      if (id && id !== selectedClientId) setSelectedClientId(id);
    };
    window.addEventListener('storage', handler);
    window.addEventListener('client-changed', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('client-changed', handler);
    };
  }, [selectedClientId]);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const currentClient = clients.find(c => c.id === selectedClientId) || clients[0];
  const isActive = (path: string) => location.pathname.startsWith(path);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && clientModalOpen) {
        setClientModalOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clientModalOpen]);
  useEffect(() => {
    if (!clientModalOpen) {
      setSearchQuery('');
    }
  }, [clientModalOpen]);
  const filteredClients = clients.filter(client => client.name.toLowerCase().includes(searchQuery.toLowerCase()));
  return <div className={`h-full flex flex-col transition-all duration-300 ${collapsed ? 'w-16' : 'w-64'}`} style={{
    backgroundColor: '#010827',
    borderRight: '1px solid rgba(255,255,255,0.08)',
    color: '#E0E0E0'
  }} data-id="element-128">
      {/* Header */}
      <div className="p-4 flex items-center justify-between" data-id="element-129">
        {!collapsed && <div className="flex flex-col leading-tight" data-id="element-130">
            <span className="font-logo text-[24px] tracking-wide" style={{
          color: '#FFFFFF'
        }} data-id="element-131">
              Esclapon
            </span>
            <span className="font-logo text-[24px] tracking-wide" style={{
          color: '#FFFFFF'
        }} data-id="element-132">
              & Co.
            </span>
          </div>}
        {collapsed && <div className="flex items-center justify-center w-full" data-id="element-133">
            <span className="font-logo text-[22px]" style={{
          color: '#FFFFFF'
        }} data-id="element-134">
              E
            </span>
          </div>}
        <Button variant="ghost" size="icon-sm" onClick={onToggle} className="ml-auto text-gray-400 hover:text-white hover:bg-white/10" data-id="element-135">
          <ChevronLeft className={`h-4 w-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} data-id="element-136" />
        </Button>
      </div>

      <div style={{
      borderTop: '1px solid rgba(255,255,255,0.08)'
    }} data-id="element-137" />

      {/* Client Selector */}
      {!collapsed ? <div className="px-4 py-3" style={{
      backgroundColor: 'rgba(255,255,255,0.04)'
    }} data-id="element-138">
          <span className="text-[11px] uppercase tracking-wider mb-1.5 block" style={{
        color: '#8B95A5'
      }} data-id="element-139">
            Cliente selezionato:
          </span>
          <div className="flex items-center justify-between gap-2" data-id="element-140">
            <div className="w-[120px] h-[30px] flex items-center flex-shrink min-w-0" data-id="element-141">
              <img src={currentClient.logo} alt={currentClient.name} className="max-w-full max-h-full object-contain object-left" style={{
            filter: 'brightness(0) invert(1)'
          }} data-id="element-142" />
            </div>
            <button onClick={() => setClientModalOpen(true)} className="flex-shrink-0 p-1 rounded-md transition-colors" style={{
          color: '#8B95A5'
        }} title="Cambia cliente" onMouseEnter={e => {
          e.currentTarget.style.color = '#fff';
          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
        }} onMouseLeave={e => {
          e.currentTarget.style.color = '#8B95A5';
          e.currentTarget.style.backgroundColor = 'transparent';
        }} data-id="element-143">
              <ChevronsUpDown className="h-4 w-4" data-id="element-144" />
            </button>
          </div>
        </div> : <div className="px-2 py-2" data-id="element-145">
          <Button variant="ghost" size="icon-sm" onClick={() => setClientModalOpen(true)} className="w-full text-gray-400 hover:text-white hover:bg-white/10" title={currentClient.name} data-id="element-146">
            <Building2 className="h-4 w-4" data-id="element-147" />
          </Button>
        </div>}

      <div style={{
      borderTop: '1px solid rgba(255,255,255,0.08)'
    }} data-id="element-148" />

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-6" data-id="element-149">
        {navSections.map((section, idx) => <div key={idx} data-id="element-150">
            {!collapsed && <div className="px-3 py-2 text-[11px] uppercase tracking-wider" style={{
          color: '#6B7585'
        }} data-id="element-151">
                {section.title}
              </div>}
            <div className="space-y-0.5" data-id="element-152">
              {section.items.map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return <Link key={item.path} to={item.path} className="block" data-id="element-153">
                    <button className="w-full flex items-center gap-3 px-3 py-2.5 transition-colors border-l-2" style={{
                borderColor: active ? '#4DB8E8' : 'transparent',
                color: active ? '#FFFFFF' : '#8B95A5',
                backgroundColor: active ? 'rgba(77,184,232,0.12)' : 'transparent'
              }} onMouseEnter={e => {
                if (!active) {
                  e.currentTarget.style.color = '#FFFFFF';
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
                }
              }} onMouseLeave={e => {
                if (!active) {
                  e.currentTarget.style.color = '#8B95A5';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }} data-id="element-154">
                      <Icon className="h-4 w-4 flex-shrink-0" style={{
                  color: active ? '#4DB8E8' : 'inherit'
                }} data-id="element-155" />
                      {!collapsed && <span className="text-sm" data-id="element-156">{item.label}</span>}
                    </button>
                  </Link>;
          })}
            </div>
          </div>)}
      </nav>

      {/* Client Selection Command Palette Modal */}
      <AnimatePresence data-id="element-157">
        {clientModalOpen && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6" data-id="element-158">
              <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setClientModalOpen(false)} data-id="element-159" />
              <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" data-id="element-160">
                <div className="flex items-center px-4 py-3 border-b border-border" data-id="element-161">
                  <Search className="h-5 w-5 text-muted-foreground mr-3 flex-shrink-0" data-id="element-162" />
                  <input type="text" autoFocus placeholder="Cerca cliente..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="flex-1 bg-transparent border-none outline-none text-base text-foreground placeholder:text-muted-foreground" data-id="element-163" />
                </div>
                <div className="max-h-[300px] overflow-y-auto p-2" data-id="element-164">
                  {filteredClients.length > 0 ? filteredClients.map(client => {
              const isSelected = client.id === selectedClientId;
              return <button key={client.id} onClick={() => {
                setSelectedClientId(client.id);
                setClientModalOpen(false);
              }} className={`w-full flex items-center gap-4 px-3 py-3 rounded-xl text-left transition-colors ${isSelected ? 'bg-accent/10 text-accent' : 'hover:bg-muted/50 text-foreground'}`} data-id="element-165">
                          <div className="w-[70px] h-[24px] flex items-center justify-center bg-white rounded flex-shrink-0" data-id="element-166">
                            <img src={client.logo} alt={client.name} className="max-w-full max-h-full object-contain" data-id="element-167" />
                          </div>
                          <span className="font-normal text-sm flex-1 truncate" data-id="element-168">
                            {client.name}
                          </span>
                          {isSelected && <Check className="h-4 w-4 text-accent flex-shrink-0" data-id="element-169" />}
                        </button>;
            }) : <div className="py-8 text-center text-sm text-muted-foreground" data-id="element-170">
                      Nessun cliente trovato.
                    </div>}
                </div>
              </motion.div>
            </div>, document.body)}
      </AnimatePresence>
    </div>;
}